/**
 * @file Shared shell helpers for parent-style modal forms.
 */

/**
 * Clones source item data for modal-local editing state.
 * @param {object | null} item - Source item data.
 * @returns {object} Mutable form item data.
 */
export function cloneFormItemData(item) {
  if (!item) {return {};}

  try {
    return structuredClone(item);
  } catch (error) {
    void error;
    return JSON.parse(JSON.stringify(item));
  }
}

/**
 * Applies add-mode parent context while preserving current form payload shape.
 * @param {object} formData - Mutable form data.
 * @param {object | null} item - Source item data.
 * @param {boolean} isEditing - Whether the form edits an existing item.
 * @returns {object} Updated form data.
 */
export function applyAddFormContext(formData, item, isEditing) {
  if (isEditing || !item) {return formData;}

  if (item.parentId) {formData.parentId = item.parentId;}
  if (item.owner) {formData.owner = item.owner;}
  delete formData.id;
  return formData;
}

/**
 * Resolves the modal title for current parent-style and legacy form callers.
 * @param {object} options - Title options.
 * @param {string} options.type - Form type.
 * @param {object | null} options.item - Source item data.
 * @param {boolean} options.isEditing - Whether the form edits an existing item.
 * @param {Function} options.findItem - Item lookup helper.
 * @returns {string} Modal title.
 */
export function getFormModalTitle({ type, item, isEditing, findItem }) {
  if (!isEditing && type === "task" && item?.parentId) {
    const parent = findItem(item.parentId);
    if (parent) {
      return `Add Task to ${
        parent.name ||
        parent.title ||
        parent.item ||
        parent.patient_name ||
        "linked item"
      }`;
    }
  }

  const label = type.replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  return `${isEditing ? "Edit" : "Add"} ${label}`;
}

/**
 * Returns header action HTML owned by the parent form shell.
 * @param {string} type - Form type.
 * @returns {string} Header action HTML.
 */
export function getFormHeaderActionsHtml(type) {
  if (type !== "task") {return "";}

  return `
    <button
      type="button"
      id="task-recurrence-toggle"
      class="mr-2 text-gray-400 transition-colors hover:text-green-700 dark:text-gray-500 dark:hover:text-green-300"
      aria-pressed="false"
      title="Recurring task"
    >
      <span class="material-symbols-outlined text-[20px] font-normal leading-none">repeat</span>
    </button>
  `;
}

/**
 * Clears modal shell content that can be left behind by specialised forms.
 * @param {HTMLElement} formModalElement - Form modal root.
 */
export function clearSpecialisedFormShellArtifacts(formModalElement) {
  const formTitle = formModalElement?.querySelector("#form-title");
  const existingBulk = formTitle?.parentElement?.querySelector(
    "#bulk-checkbox-container",
  );

  if (existingBulk) {existingBulk.remove();}
}
